import { DrawerActions } from '@react-navigation/native';
import { Stack, useNavigation, useRouter } from 'expo-router';
import React from 'react';
import { ActivityIndicator, FlatList, Pressable, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { IconSymbol } from '@/components/ui/IconSymbol';
import AppStyles from '@/constants/Styles';
import { useBackgroundLeafletingRounds } from '@/hooks/useBackgroundFetch';
import { useThemeColor } from '@/hooks/useThemeColor';
import { LeafletingRound } from '@/types/leafleting';

export default function LeafletingIndexScreen() {
  // Use background fetch hook for leafleting rounds
  const { data: rounds, loading, error } = useBackgroundLeafletingRounds();
  const navigation = useNavigation();
  const router = useRouter();
  
  // Theme-aware colors
  const backgroundColor = useThemeColor({}, 'background');
  const cardBackgroundColor = useThemeColor({}, 'cardBackground');
  const borderColor = useThemeColor({}, 'border');
  const secondaryTextColor = useThemeColor({}, 'secondaryText');
  const insets = useSafeAreaInsets();
  
  // Use existing progress bar colors from the app's color system
  const progressHighColor = useThemeColor({}, 'progressHigh');     // Green - completed
  const progressMediumColor = useThemeColor({}, 'progressMedium'); // Orange - in progress
  const errorColor = useThemeColor({}, 'errorText');               // Red - assigned (needs attention)
  const progressLowColor = useThemeColor({}, 'progressLow');       // Grey - unassigned/not doing
  const progressBackgroundColor = useThemeColor({}, 'progressBackground'); // Background color

  const renderLeafletingProgressBar = (round: LeafletingRound) => {
    return (
      <View style={AppStyles.progressBarContainer}>
        <View style={[AppStyles.progressBarBackground, { backgroundColor: progressBackgroundColor }]}>
          {/* Completed - Green (High Progress) */}
          {round.completed_count > 0 && (
            <View style={[
              AppStyles.progressBarSegment,
              { 
                width: `${(round.completed_count / round.total_assignments) * 100}%`,
                backgroundColor: progressHighColor
              }
            ]} />
          )}
          
          {/* In Progress - Orange (Medium Progress) */}
          {round.in_progress_count > 0 && (
            <View style={[
              AppStyles.progressBarSegment,
              { 
                width: `${(round.in_progress_count / round.total_assignments) * 100}%`,
                backgroundColor: progressMediumColor
              }
            ]} />
          )}
          
          {/* Assigned - Red (Needs Attention) */}
          {round.assigned_count > 0 && (
            <View style={[
              AppStyles.progressBarSegment,
              { 
                width: `${(round.assigned_count / round.total_assignments) * 100}%`,
                backgroundColor: errorColor
              }
            ]} />
          )}
        </View>
        
        {/* Progress Bar Labels - All on one line */}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginTop: 6 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 8, marginBottom: 4 }}>
            <View style={{ width: 8, height: 8, backgroundColor: progressHighColor, borderRadius: 4, marginRight: 4 }} />
            <ThemedText style={{ fontSize: 12, color: secondaryTextColor }}>
              Completed: {round.completed_count}
            </ThemedText>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 8, marginBottom: 4 }}>
            <View style={{ width: 8, height: 8, backgroundColor: progressMediumColor, borderRadius: 4, marginRight: 4 }} />
            <ThemedText style={{ fontSize: 12, color: secondaryTextColor }}>
              In Progress: {round.in_progress_count}
            </ThemedText>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 8, marginBottom: 4 }}>
            <View style={{ width: 8, height: 8, backgroundColor: errorColor, borderRadius: 4, marginRight: 4 }} />
            <ThemedText style={{ fontSize: 12, color: secondaryTextColor }}>
              Assigned: {round.assigned_count}
            </ThemedText>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 8, marginBottom: 4 }}>
            <View style={{ width: 8, height: 8, backgroundColor: progressLowColor, borderRadius: 4, marginRight: 4 }} />
            <ThemedText style={{ fontSize: 12, color: secondaryTextColor }}>
              Unassigned: {round.unassigned_count}
            </ThemedText>
          </View>
          {/* Not Doing (only show if > 0) */}
          {round.not_doing_count > 0 && (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 8, marginBottom: 4 }}>
              <View style={{ width: 8, height: 8, backgroundColor: progressLowColor, borderRadius: 4, marginRight: 4 }} />
              <ThemedText style={{ fontSize: 12, color: secondaryTextColor }}>
                Not Doing: {round.not_doing_count}
              </ThemedText>
            </View>
          )}
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" />
        <ThemedText style={{ marginTop: 16 }}>Loading leafleting rounds...</ThemedText>
      </ThemedView>
    );
  }

  if (error) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ThemedText style={AppStyles.errorText}>
          Error loading leafleting rounds: {error.message}
        </ThemedText>
      </ThemedView>
    );
  }

  return (
    <ThemedView style={[AppStyles.container, { backgroundColor }]}>
      <Stack.Screen
        options={{
          headerTitle: () => (
            <View style={AppStyles.headerContainer}>
              <ThemedText style={AppStyles.headerText}>Leafleting Rounds</ThemedText>
            </View>
          ),
          headerLeft: () => (
            <TouchableOpacity onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())} style={{ paddingLeft: 15 }}>
              <IconSymbol name="chevron.right" size={28} color="#007AFF" />
            </TouchableOpacity>
          ),
          headerBackVisible: false,
        }}
      />
      
      {loading && <ActivityIndicator size="large" color={useThemeColor({}, 'icon')} />}
      {error && <ThemedText style={AppStyles.errorText}>{error}</ThemedText>}
      
      {!loading && !error && (!rounds || rounds.length === 0) && (
        <View style={AppStyles.emptyContainer}>
          <ThemedText style={AppStyles.noDataText}>No leafleting rounds found</ThemedText>
        </View>
      )}
      {!loading && !error && rounds && rounds.length > 0 && (
        <FlatList
          data={rounds}
          keyExtractor={(item) => item.round_global_id}
          renderItem={({ item }: { item: LeafletingRound }) => (
            <Pressable 
              style={[
                AppStyles.listItemLeftAligned, 
                { backgroundColor: cardBackgroundColor, borderBottomColor: borderColor }
              ]} 
              onPress={() => {
                router.push(`./leafleting/${item.round_global_id}` as any);
              }}
            >
              <View style={{ width: '100%' }}>
                <ThemedText style={AppStyles.headerText}>{item.round_name}</ThemedText>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <ThemedText style={{ color: secondaryTextColor, fontSize: 14 }}>
                    Total assignments: {item.total_assignments}
                  </ThemedText>
                </View>
                {renderLeafletingProgressBar(item)}
              </View>
            </Pressable>
          )}
          contentContainerStyle={{ paddingBottom: insets.bottom }}
        />
      )}
    </ThemedView>
  );
}
